import { supabaseAuth } from './supabaseAuth';
import { csrfService } from './csrfService';
import { logger } from '../utils/logger';

function getApiBase(): string {
  const env = (typeof process !== 'undefined' ? (process as { env?: Record<string, string> }).env : {}) || {};
  const apiUrl = env.REACT_APP_API_URL || env.VITE_API_URL || '/api';
  return apiUrl.includes('://')
    ? (apiUrl.endsWith('/api') ? apiUrl : apiUrl.replace(/\/$/, '') + '/api')
    : apiUrl;
}

/**
 * End Supabase session and clear client storage, then return to app root.
 * Matches previous behavior (full clear) after proper server sign-out.
 */
export async function signOutCompletely(): Promise<void> {
  try {
    await supabaseAuth.signOut();
  } catch (e) {
    logger.warn('supabase signOut', e);
  }
  try {
    localStorage.clear();
    sessionStorage.clear();
  } catch {
    // ignore
  }
  window.location.href = '/';
}

/**
 * Permanently delete the current user's account (server-side). Caller should sign out after success.
 */
export async function deleteAccountPermanently(): Promise<{ success: boolean; error?: string }> {
  try {
    const accessToken = await supabaseAuth.getAccessToken();
    if (!accessToken) {
      return { success: false, error: 'Not signed in' };
    }

    // Get CSRF token for secure request
    const csrfToken = await csrfService.getCSRFToken();

    const response = await fetch(`${getApiBase()}/delete-account`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${accessToken}`,
        ...(csrfToken && { 'X-CSRF-Token': csrfToken })
      },
    });
    const data = (await response.json().catch(() => ({}))) as { error?: string };
    if (!response.ok) {
      return { success: false, error: data.error || `Request failed (${response.status})` };
    }
    return { success: true };
  } catch (e: unknown) {
    const err = e as Error;
    return { success: false, error: err?.message || 'Network error' };
  }
}

export async function cancelSubscriptionAtPeriodEnd(
  userId: string,
  stripeSubscriptionId: string,
  reason?: string
): Promise<{ success: boolean; error?: string }> {
  try {
    const accessToken = await supabaseAuth.getAccessToken();
    if (!accessToken) {
      return { success: false, error: 'Please sign in to manage your subscription.' };
    }

    // Get CSRF token for secure request
    const csrfToken = await csrfService.getCSRFToken();

    const response = await fetch(`${getApiBase()}/cancel-subscription`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${accessToken}`,
        ...(csrfToken && { 'X-CSRF-Token': csrfToken })
      },
      body: JSON.stringify({ userId, stripeSubscriptionId, ...(reason ? { reason } : {}) }),
    });
    const data = (await response.json().catch(() => ({}))) as { error?: string };
    if (!response.ok) {
      return { success: false, error: data.error || `Request failed (${response.status})` };
    }
    return { success: true };
  } catch (e: unknown) {
    const err = e as Error;
    return { success: false, error: err?.message || 'Network error' };
  }
}
